import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  Col,
  Form,
  Input,
  Row,
  Select,
  Switch,
  Table,
  Typography,
  Modal,
  Space,
  Tag,
} from "antd";
import useRequest from "../hooks/useRequest";
import { Severty, ShowToast } from "../helper/toast";
import apiPath from "../constants/apiPath";
import {
  EditOutlined,
  EyeOutlined,
  DeleteOutlined,
  ExclamationCircleFilled,
  SearchOutlined,
  CalendarOutlined,
} from "@ant-design/icons";

const { Title } = Typography;
const { Option } = Select;

const roles = ["Admin", "Ops", "Vendor", "Client"];
const statusOptions = ["Active", "Inactive"];

const Ops = () => {
  const { request } = useRequest();
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [form] = Form.useForm();
  const [searchForm] = Form.useForm();
  const [editingUser, setEditingUser] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [viewingUser, setViewingUser] = useState(null);
  const [deletingUser, setDeletingUser] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Search and filter states
  const [searchKeyword, setSearchKeyword] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  // Debounce state
  const [searchTimeout, setSearchTimeout] = useState(null);

  // Fetch users with pagination and filters
  const fetchUsers = (page = 1, pageSize = 10, filters = {}) => {
    setLoading(true);

    // Build query parameters
    let queryParams = `page=${page}&page_size=${pageSize}&role=Ops`;

    // Add search keyword if provided
    if (filters.searchKeyword && filters.searchKeyword.trim()) {
      queryParams += `&search=${filters.searchKeyword.trim()}`;
    }

    // Add status filter if provided - send 1 for Active, 0 for Inactive
    if (filters.status && filters.status !== "all") {
      queryParams += `&status=${filters.status}`;
    }

    request({
      url: `${apiPath.opsList}?${queryParams}`,
      method: "GET",
      onSuccess: (response) => {
        setUsers(response?.data || []);
        setPagination({
          current: response?.pagination?.current_page || 1,
          pageSize: response?.pagination?.page_size || 10,
          total: response?.pagination?.total_count || 0,
        });
        setLoading(false);
      },
      onError: () => {
        setLoading(false);
        ShowToast("Failed to fetch ops users", Severty.ERROR);
      },
    });
  };

  useEffect(() => {
    fetchUsers(pagination.current, pagination.pageSize);
  }, []);

  // Handle page change
  const handleTableChange = (pagination) => {
    fetchUsers(pagination.current, pagination.pageSize, {
      searchKeyword,
      status: statusFilter,
    });
  };

  // Handle search with debounce
  const handleSearch = (keyword) => {
    // Clear existing timeout
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    // Set new timeout for debounce (500ms delay)
    const timeout = setTimeout(() => {
      setSearchKeyword(keyword);
      fetchUsers(1, pagination.pageSize, {
        searchKeyword: keyword,
        status: statusFilter,
      });
    }, 500); // 500ms delay

    setSearchTimeout(timeout);
  };

  // Handle search input change
  const handleSearchChange = (e) => {
    const value = e.target.value;
    handleSearch(value);
  };

  // Handle form submission (if user still presses enter)
  const handleSearchSubmit = (values) => {
    const { keyword } = values;
    // Clear any existing timeout to avoid duplicate calls
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    setSearchKeyword(keyword);
    fetchUsers(1, pagination.pageSize, {
      searchKeyword: keyword,
      status: statusFilter,
    });
  };

  // Handle status filter change - convert to 1 for Active, 0 for Inactive
  const handleStatusFilterChange = (value) => {
    let statusValue;

    if (value === "active") {
      statusValue = "1";
    } else if (value === "inactive") {
      statusValue = "0";
    } else {
      statusValue = "all";
    }

    setStatusFilter(statusValue);
    fetchUsers(1, pagination.pageSize, {
      searchKeyword,
      status: statusValue,
    });
  };

  // Reset filters
  const resetFilters = () => {
    setSearchKeyword("");
    setStatusFilter("all");
    searchForm.resetFields();
    
    // Clear any pending search timeout
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    
    fetchUsers(1, pagination.pageSize);
  };

  // Clean up timeout on component unmount
  useEffect(() => {
    return () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
    };
  }, [searchTimeout]);

  // Format date function
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return "Invalid Date";
    }
  };

  // Get relative time (e.g., "2 days ago")
  const getRelativeTime = (dateString) => {
    if (!dateString) return "N/A";
    
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffInMs = now - date;
      const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
      
      if (diffInDays === 0) {
        const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
        if (diffInHours === 0) {
          const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
          return `${diffInMinutes} minute${diffInMinutes !== 1 ? 's' : ''} ago`;
        }
        return `${diffInHours} hour${diffInHours !== 1 ? 's' : ''} ago`;
      } else if (diffInDays === 1) {
        return "Yesterday";
      } else if (diffInDays < 7) {
        return `${diffInDays} days ago`;
      } else if (diffInDays < 30) {
        const weeks = Math.floor(diffInDays / 7);
        return `${weeks} week${weeks !== 1 ? 's' : ''} ago`;
      } else {
        const months = Math.floor(diffInDays / 30);
        return `${months} month${months !== 1 ? 's' : ''} ago`;
      }
    } catch (error) {
      return "N/A";
    }
  };

  const openModal = (user = null) => {
    setEditingUser(user);
    form.resetFields();
    if (user) form.setFieldsValue(user);
    else form.setFieldsValue({ role: "Ops", status: true }); // Set default role for new user
    setModalVisible(true);
  };

  const onFinish = (values) => {
    setLoading(true);
    const payload = {
      ...values,
      status: values.status ? 1 : 0,
    };

    // Explicitly set role to "Ops" for new users
    if (!editingUser) {
      payload.role = "Ops";
    }

    const apiCall = editingUser
      ? request({
          url: `/user/update_user`,
          method: "PUT",
          data: { ...payload, id: editingUser.id },
          onSuccess: () => {
            ShowToast("Ops user updated successfully", Severty.SUCCESS);
            fetchUsers(pagination.current, pagination.pageSize, {
              searchKeyword,
              status: statusFilter,
            });
            setModalVisible(false);
            setLoading(false);
          },
          onError: (err) => {
            ShowToast(err?.response?.data?.error, Severty.ERROR);
            setLoading(false);
          },
        })
      : request({
          url: "/user/create_user",
          method: "POST",
          data: payload,
          onSuccess: () => {
            ShowToast(
              "Ops user added successfully. Welcome email sent!",
              Severty.SUCCESS
            );
            fetchUsers(pagination.current, pagination.pageSize, {
              searchKeyword,
              status: statusFilter,
            });
            setModalVisible(false);
            setLoading(false);
          },
          onError: (err) => {
            ShowToast(err?.response?.data?.error, Severty.ERROR);
            setLoading(false);
          },
        });
  };

  const toggleStatus = (record) => {
    const newStatus = record.status === 1 ? 0 : 1;
    request({
      url: `/user/update_status`,
      method: "PUT",
      data: {
        id: record.id,
        status: newStatus,
      },
      onSuccess: () => {
        ShowToast("Ops user status updated successfully", Severty.SUCCESS);
        fetchUsers(pagination.current, pagination.pageSize, {
          searchKeyword,
          status: statusFilter,
        });
      },
      onError: (err) => ShowToast(err?.response?.data?.message, Severty.ERROR),
    });
  };

  // Open delete confirmation modal
  const openDeleteModal = (user) => {
    setDeletingUser(user);
    setDeleteModalVisible(true);
  };

  // Close delete confirmation modal
  const closeDeleteModal = () => {
    setDeleteModalVisible(false);
    setDeletingUser(null);
    setDeleteLoading(false);
  };

  // Delete user function
  const handleDeleteUser = () => {
    if (!deletingUser) return;

    setDeleteLoading(true);
    request({
      url: `/user/delete_user`,
      method: "DELETE",
      data: { id: deletingUser.id },
      onSuccess: () => {
        ShowToast("Ops user deleted successfully", Severty.SUCCESS);
        fetchUsers(pagination.current, pagination.pageSize, {
          searchKeyword,
          status: statusFilter,
        });
        closeDeleteModal();
      },
      onError: (err) => {
        const errorMessage = err?.response?.data?.error || "Failed to delete ops user";
        ShowToast(errorMessage, Severty.ERROR);
        setDeleteLoading(false);
      },
    });
  };

  const columns = [
    { 
      title: "Name", 
      dataIndex: "name", 
      key: "name" 
    },
    { 
      title: "Email", 
      dataIndex: "email", 
      key: "email" 
    },
    { 
      title: "Role", 
      dataIndex: "role", 
      key: "role" 
    },
    {
      title: "Created At",
      dataIndex: "created_at",
      key: "created_at",
      render: (createdAt, record) => (
        <div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            <CalendarOutlined style={{ marginRight: '4px' }} />
            {formatDate(createdAt)}
          </div>
          <div style={{ fontSize: '11px', color: '#999' }}>
            {getRelativeTime(createdAt)}
          </div>
        </div>
      ),
      sorter: (a, b) => new Date(a.created_at) - new Date(b.created_at),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (text, record) => (
        <Switch checked={text === 1} onChange={() => toggleStatus(record)} />
      ),
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <div className="d-flex gap-2">
          <Button onClick={() => openModal(record)}>
            <EditOutlined />
          </Button>
          <Button
            onClick={() => {
              setViewingUser(record);
              setViewModalVisible(true);
            }}
          >
            <EyeOutlined />
          </Button>
          <Button danger onClick={() => openDeleteModal(record)}>
            <DeleteOutlined />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="layout-content">
      <Card>
        <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
          <Col>
            <Title level={3}>Manage Ops Accounts</Title>
          </Col>
          <Col>
            <Button type="primary" onClick={() => openModal()}>
              Add New Ops User
            </Button>
          </Col>
        </Row>

        {/* Search and Filter Section */}
        <Card size="small" style={{ marginBottom: 16 }}>
          <Form
            form={searchForm}
            layout="vertical"
            onFinish={handleSearchSubmit}
            initialValues={{
              keyword: "",
              status: "all",
            }}
          >
            <Row gutter={[16, 16]} align="bottom">
              {/* Search Input */}
              <Col xs={24} sm={12} md={8} lg={6}>
                <Form.Item label="Search Ops Users" name="keyword">
                  <Input
                    placeholder="Search by name or email..."
                    prefix={<SearchOutlined />}
                    allowClear
                    onChange={handleSearchChange}
                    onPressEnter={(e) => {
                      e.preventDefault();
                      handleSearchSubmit(searchForm.getFieldsValue());
                    }}
                  />
                </Form.Item>
              </Col>

              {/* Status Filter */}
              <Col xs={24} sm={12} md={6} lg={4}>
                <Form.Item label="Status" name="status">
                  <Select onChange={handleStatusFilterChange}>
                    <Option value="all">All Status</Option>
                    <Option value="active">Active</Option>
                    <Option value="inactive">Inactive</Option>
                  </Select>
                </Form.Item>
              </Col>

              {/* Action Buttons */}
              <Col xs={24} sm={12} md={6} lg={4}>
                <Form.Item label=" " colon={false}>
                  <Space>
                    <Button
                      type="primary"
                      htmlType="submit"
                      icon={<SearchOutlined />}
                    >
                      Search
                    </Button>
                    {/* <Button onClick={resetFilters}>Reset</Button> */}
                  </Space>
                </Form.Item>
              </Col>
            </Row>
          </Form>

          {/* Active Filters Display */}
          {(searchKeyword || statusFilter !== "all") && (
            <div style={{ marginTop: 16, padding: 8, background: "#f0f8ff", borderRadius: 6 }}>
              <Space wrap>
                <strong>Active Filters:</strong>
                {searchKeyword && (
                  <span style={{ background: "#e6f7ff", padding: "2px 8px", borderRadius: 4 }}>
                    Search: "{searchKeyword}"
                  </span>
                )}
                {statusFilter !== "all" && (
                  <span style={{ background: "#e6f7ff", padding: "2px 8px", borderRadius: 4 }}>
                    Status: {statusFilter === "1" ? "Active" : "Inactive"}
                  </span>
                )}
                <Button type="link" size="small" onClick={resetFilters}>
                  Clear All
                </Button>
              </Space>
            </div>
          )}
        </Card>

        <Table
          columns={columns}
          dataSource={users}
          rowKey="id"
          loading={loading}
          pagination={pagination}
          onChange={handleTableChange}
          className="mt-3"
        />
      </Card>

      {/* Add/Edit User Modal */}
      <Modal
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        className="tab_modal"
      >
        <div className="modal_header">
          {editingUser ? "Edit Ops User" : "Add New Ops User"}
        </div>
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          initialValues={{ role: "Ops", status: true }}
        >
          <Form.Item
            label="Name"
            name="name"
            rules={[{ required: true, message: "Please enter name" }]}
          >
            <Input placeholder="Name" />
          </Form.Item>

          <Form.Item
            label="Email"
            name="email"
            rules={[
              { required: true, message: "Please enter email" },
              { type: "email", message: "Please enter a valid email" },
            ]}
            disabled={!!editingUser}
          >
            <Input placeholder="Email" />
          </Form.Item>

          <Form.Item
            label="Role"
            name="role"
            rules={[{ required: true, message: "Role is required" }]}
          >
            <Input value="Ops" disabled style={{ background: "#f0f0f0" }} />
          </Form.Item>

          <Form.Item>
            <div className="modal_footer">
              <Button type="primary" htmlType="submit" loading={loading}>
                {editingUser ? "Update Ops User" : "Create Ops User"}
              </Button>
            </div>
          </Form.Item>
        </Form>
      </Modal>

      {/* View User Modal */}
      <Modal
        open={viewModalVisible}
        onCancel={() => setViewModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setViewModalVisible(false)}>
            Close
          </Button>,
        ]}
        className="tab_modal"
      >
        <div className="modal_header">
          Ops User Details
        </div>
        {viewingUser && (
          <div>
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <strong>Name:</strong> {viewingUser.name}
              </Col>
              <Col span={12}>
                <strong>Email:</strong> {viewingUser.email}
              </Col>
              <Col span={12}>
                <strong>Role:</strong> {viewingUser.role}
              </Col>
              <Col span={12}>
                <strong>Created At:</strong> {formatDate(viewingUser.created_at)}
              </Col>
              <Col span={12}>
                <strong>Account Age:</strong> {getRelativeTime(viewingUser.created_at)}
              </Col>
              <Col span={12}>
                <strong>Status:</strong>{" "}
                {viewingUser.status === 1 ? "Active" : "Inactive"}
              </Col>
              <Col span={12}>
                <strong>Superuser:</strong>{" "}
                {viewingUser.is_superuser ? "Yes" : "No"}
              </Col>
              <Col span={12}>
                <strong>Email Verified:</strong>{" "}
                {viewingUser.is_email_verified ? "Yes" : "No"}
              </Col>
              <Col span={12}>
                <strong>Approved:</strong>{" "}
                {viewingUser.is_approved ? "Yes" : "No"}
              </Col>
              <Col span={12}>
                <strong>Phone:</strong>{" "}
                {viewingUser.phone_number || "Not provided"}
              </Col>
              {viewingUser.user_detail && (
                <>
                  <Col span={12}>
                    <strong>Company:</strong>{" "}
                    {viewingUser.user_detail.company || "Not provided"}
                  </Col>
                  <Col span={12}>
                    <strong>Language:</strong>{" "}
                    {viewingUser.user_detail.preferred_language || "Not set"}
                  </Col>
                </>
              )}
            </Row>
          </div>
        )}
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        open={deleteModalVisible}
        onCancel={closeDeleteModal}
        footer={[
          <Button key="cancel" onClick={closeDeleteModal}>
            Cancel
          </Button>,
          <Button
            key="delete"
            type="primary"
            danger
            loading={deleteLoading}
            onClick={handleDeleteUser}
          >
            Delete Ops User
          </Button>,
        ]}
        className="tab_modal"
      >
        <div className="modal_header">
          <ExclamationCircleFilled style={{ color: "#faad14", marginRight: 8 }} />
          Delete Ops User
        </div>
        <div style={{ padding: "20px 0" }}>
          <p>Are you sure you want to delete this ops user?</p>
          {deletingUser && (
            <div style={{ background: "#f5f5f5", padding: "12px", borderRadius: "6px", marginTop: "12px" }}>
              <strong>Ops User Details:</strong>
              <div>Name: {deletingUser.name}</div>
              <div>Email: {deletingUser.email}</div>
              <div>Role: {deletingUser.role}</div>
              <div>Created: {formatDate(deletingUser.created_at)}</div>
            </div>
          )}
          <p style={{ color: "#ff4d4f", marginTop: "12px" }}>
            <strong>Warning:</strong> This action cannot be undone.
          </p>
        </div>
      </Modal>
    </div>
  );
};

export default Ops;